"use client"

/**
 * TabListPopover — shows a popover listing all open tabs in a tabset.
 *
 * Rendered as the "leading" icon in each tabset header bar.
 * Clicking the Layers icon opens a popover with all tab names;
 * clicking a tab name activates (selects) that tab.
 */

import { useState, useCallback } from "react"
import { Actions, type Model, type TabSetNode, type TabNode } from "flexlayout-react"
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover"

interface TabListPopoverProps {
  tabSetNode: TabSetNode
  model: Model
}

export function TabListPopover({ tabSetNode, model }: TabListPopoverProps) {
  const [open, setOpen] = useState(false)

  const tabs = tabSetNode.getChildren() as TabNode[]
  const selectedId = tabSetNode.getSelectedNode()?.getId()

  const handleSelectTab = useCallback(
    (tabId: string) => {
      model.doAction(Actions.selectTab(tabId))
      setOpen(false)
    },
    [model],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="fl-layers-icon"
          title="Open tabs in this panel"
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setOpen((prev) => !prev)
          }}
        >
          <img src="/ic_tabs_list.svg" alt="" className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={6}
        className="w-56 p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-0.5">
          <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            Open tabs ({tabs.length})
          </p>
          {tabs.map((tab) => {
            const isActive = tab.getId() === selectedId
            return (
              <button
                key={tab.getId()}
                type="button"
                onClick={() => handleSelectTab(tab.getId())}
                className={`flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm text-left transition-colors
                  ${
                    isActive
                      ? "bg-accent text-accent-foreground font-medium"
                      : "text-foreground hover:bg-accent/50"
                  }`}
              >
                <span className="truncate">{tab.getName()}</span>
                {isActive && (
                  <span className="ml-auto shrink-0 h-1.5 w-1.5 rounded-full bg-blue-500" />
                )}
              </button>
            )
          })}
          {tabs.length === 0 && (
            <p className="px-2 py-3 text-xs text-muted-foreground text-center">
              No open tabs
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
